import { BaseQueryFn } from '@reduxjs/toolkit/query';
import { AxiosRequestConfig, AxiosError, AxiosResponse } from 'axios';
import ApiClient from './ApiClient';
import { RootState } from '../store/store';
import { logoutDueToError } from '@/store/authSlice';
import { ErrorResponse } from '@/lib/Types/CommonTypes';
import { toast } from 'react-toastify';
import { isPlatformAdminUser } from '@/utils/userRole';
import { navigateTo } from '@/utils/navigate';

export interface BaseQueryParams<D = unknown, P = unknown> {
  url: string;
  method: AxiosRequestConfig['method'];
  data?: D;
  params?: P;
  responseType?: AxiosRequestConfig['responseType'];
  headers?: AxiosRequestConfig['headers']; // optional per-request header overrides
}

interface SerializedError {
  message: string;
  stack?: string;
  name?: string;
  cause?: SerializedError;
  [key: string]: unknown;
}

const specificRoutes = [
  '/cms',
  'coastal-areas',
  'regions',
  'pois',
  'clubs',
  'club-resources',
  'communities',
  'list-values',
];

export const baseQuery: BaseQueryFn<BaseQueryParams, unknown, unknown> = async (
  { url, method, data, params, responseType, headers: overrideHeaders },
  api
) => {
  try {
    const state = api.getState() as RootState;
    const token = state.auth.token;
    const language = state.auth.language || 'en';

    const isSpecificRoute = specificRoutes.some((route) => url.includes(route));

    const headers: AxiosRequestConfig['headers'] = {
      Authorization: token ? `Bearer ${token}` : '',
      is_admin_panel: 'true',
    };

    if (isSpecificRoute) {
      headers['x-lang'] = language;
    } else {
      headers['x-lang'] = 'en';
    }

    const result: AxiosResponse = await ApiClient({
      url,
      method,
      data,
      params,
      headers: { ...headers, ...(overrideHeaders || {}) },
      responseType,
    });

    return { data: result.data };
  } catch (axiosError) {
    const err = axiosError as AxiosError;

    if (err?.response?.status === 403) {
      const roles = (api.getState() as RootState).auth.userData?.roles;
      if (!isPlatformAdminUser(roles)) {
        navigateTo('/error/404');
        return { data: null };
      }
      const parsedForbidden = await parseErrorData(err.response?.data);
      const errorMessage = (parsedForbidden as ErrorResponse)?.message;
      toast.error(errorMessage);
    }

    if (err?.response?.status === 401) {
      const roles = (api.getState() as RootState).auth.userData?.roles;
      if (!isPlatformAdminUser(roles)) {
        api.dispatch(logoutDueToError());
      }
    }

    return {
      error: {
        status: err.response?.status,
        data: await parseErrorData(err.response?.data || err.message),
      },
    };
  }
};

export const handleError = (
  error: { status?: number; data?: unknown; message?: string } | SerializedError
) => {
  if (isSerializedError(error)) {
    toast.error(error.message || 'An unknown error occurred.');
  } else {
    const errorMessage = getErrorMessage(error.data);
    toast.error(errorMessage || 'An unknown error occurred.');
  }
};
const getErrorMessage = (data: unknown): string | undefined => {
  if (typeof data === 'object' && data !== null) {
    if (
      'message' in data &&
      typeof (data as { message?: unknown }).message === 'string'
    ) {
      return (data as { message?: string }).message;
    }

    if ('errors' in data && (data as { errors?: unknown }).errors) {
      const errors = (data as { errors?: unknown }).errors;
      if (typeof errors === 'object' && errors !== null) {
        const messages: string[] = [];
        Object.values(errors as Record<string, unknown>).forEach((value) => {
          if (Array.isArray(value)) {
            value.forEach((item) => {
              if (typeof item === 'string') {
                messages.push(item);
              }
            });
          } else if (typeof value === 'string') {
            messages.push(value);
          }
        });

        if (messages.length > 0) {
          return messages.join('\n');
        }
      }
    }
  }
  return undefined;
};

function isSerializedError<T = unknown>(error: T) {
  return error && typeof error === 'object' && 'message' in error;
}

async function parseErrorData(data: unknown): Promise<unknown> {
  if (!data || typeof data !== 'object') return data;

  const candidate = data as {
    text?: () => Promise<string>;
    arrayBuffer?: () => Promise<ArrayBuffer>;
  };
  const looksLikeBlob =
    typeof candidate.text === 'function' ||
    typeof candidate.arrayBuffer === 'function' ||
    Object.prototype.toString.call(data) === '[object Blob]';

  if (!looksLikeBlob) return data;

  try {
    let text = '';
    if (typeof candidate.text === 'function') {
      text = await candidate.text();
    } else if (typeof candidate.arrayBuffer === 'function') {
      text = new TextDecoder().decode(await candidate.arrayBuffer());
    } else if (typeof Response !== 'undefined') {
      text = await new Response(data as BodyInit).text();
    } else {
      return data;
    }
    return JSON.parse(text);
  } catch {
    return data;
  }
}
